import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import directoryService from '../services/directoryService';
import { jwtDecode } from 'jwt-decode';

const Dashboard = ({ onLogout }) => {
  const [directories, setDirectories] = useState([]);
  const [newDirectory, setNewDirectory] = useState('');
  const [selectedDirectory, setSelectedDirectory] = useState(null);
  const [filesInSelectedDirectory, setFilesInSelectedDirectory] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  
  // Estados para gestión de directorios
  const [activeMenu, setActiveMenu] = useState(null); // Directorio con el menú de 3 puntos abierto
  const [showModal, setShowModal] = useState(null); // 'rename' | 'delete' | null
  const [modalData, setModalData] = useState(null); // Nombre del directorio afectado
  const [renameValue, setRenameValue] = useState(''); // Valor del input al renombrar
  const [isProcessing, setIsProcessing] = useState(false); // Bloqueo de UI por carga
  
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Capture - Andromeda";
    // Cerrar menú al hacer clic fuera
    const closeMenu = () => setActiveMenu(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const handleLogout = useCallback(() => {
    onLogout();
    navigate('/');
  }, [onLogout, navigate]);

  const fetchDirectories = useCallback(async () => {
    try {
      const { data } = await directoryService.getDirectories();
      setDirectories(data);
    } catch (error) {
      console.error('Error fetching directories:', error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUsername(decodedToken.username);
        setRole(decodedToken.rol);
      } catch (error) {
        console.error('Error decoding token:', error);
        handleLogout(); // Log out if token is invalid
      }
    }
    fetchDirectories();
  }, [fetchDirectories, handleLogout]);

  const handleCreateDirectory = async () => {
    // Validación para evitar directorios vacíos o con puros espacios
    if (!newDirectory || !newDirectory.trim()) {
      alert('Por favor, introduzca un nombre válido para el directorio.');
      return;
    }

    try {
      setIsProcessing(true);
      await directoryService.createDirectory({ directoryName: newDirectory.trim() });
      setNewDirectory('');
      fetchDirectories();
    } catch (error) {
      console.error('Error creating directory:', error);
      // Captura amigable si se excede el límite del plan asignado en MySQL
      const errMsg = error.response?.data?.message || 'Error al crear el directorio. Verifique los límites de su plan.';
      alert(errMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDirectoryClick = async (directoryName) => {
    setSelectedDirectory(directoryName);
    setSelectedFile(null); // Clear selected file when changing directory
    try {
      const { data } = await directoryService.getFilesInDirectory(directoryName);
      setFilesInSelectedDirectory(data);
    } catch (error) {
      console.error('Error fetching files in directory:', error);
      setFilesInSelectedDirectory([]);
    }
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
  };

  const handleFileUpload = async (directoryName) => {
    if (!selectedFile) {
      alert('Please select a file to upload.');
      return;
    }
    try {
      setIsProcessing(true);
      
      // Step A: Request the Write SAS token and pre-register metadata
      const sasResponse = await directoryService.getUploadSasUrl(directoryName, selectedFile.name);
      const { sasUrl } = sasResponse.data;

      // Step B: Upload file directly to Azure Blob Storage
      await directoryService.uploadFileDirectlyToBlob(sasUrl, selectedFile);
      
      alert('File uploaded successfully!');
      
      // Limpieza del archivo seleccionado en el estado local para evitar inconsistencias visuales
      setSelectedFile(null);
      
      // Refresh files in the current directory after upload
      if (selectedDirectory) {
        handleDirectoryClick(selectedDirectory);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      // Muestra el mensaje exacto enviado por el backend (ej: cuota de facturas excedida)
      const errMsg = error.response?.data?.message || 'File upload failed! Check your plan restrictions or connection.';
      alert(errMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteFile = async (directoryName, fileName, event) => {
    event.stopPropagation(); // Prevent triggering handleFileClick
    if (window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      try {
        setIsProcessing(true);
        await directoryService.deleteFile(directoryName, fileName);
        alert('File deleted successfully!');
        // Refresh files in the current directory after deletion
        if (selectedDirectory) {
          handleDirectoryClick(selectedDirectory);
        }
        setSelectedFile(null); // Clear selected file if it was the one deleted
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('File deletion failed!');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // Acciones de Gestión de Directorios
  const onRenameSubmit = async () => {
    if (!renameValue.trim() || renameValue.trim() === modalData) {
      setShowModal(null);
      return;
    }
    try {
      setIsProcessing(true);
      await directoryService.renameDirectory(modalData, renameValue.trim());
      await fetchDirectories();
      if (selectedDirectory === modalData) {
        setSelectedDirectory(renameValue.trim());
      }
      setShowModal(null);
    } catch (error) {
      alert(error.response?.data?.message || 'Error renaming directory');
    } finally {
      setIsProcessing(false);
    }
  };

  const onDeleteConfirm = async () => {
    try {
      setIsProcessing(true);
      await directoryService.deleteDirectory(modalData);
      await fetchDirectories();
      if (selectedDirectory === modalData) {
        setSelectedDirectory(null);
        setFilesInSelectedDirectory([]);
      }
      setShowModal(null);
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting directory');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="dashboard-page" style={{ display: 'flex', minHeight: '100vh', flexDirection: 'column', backgroundColor: '#e6f0e9' }}>
      <style>{`
        .dashboard-header { position: relative; border-bottom: 2px solid #007A33; backgroundColor: #e6f0e9; min-height: 80px; display: flex; align-items: center; justify-content: center; }
        .dashboard-logo { position: absolute; top: 50%; right: 20px; transform: translateY(-50%); width: 100px; height: auto; }
        .dashboard-main { display: flex; flex: 1; }
        .panel-dirs { width: 280px; border-right: 1px solid #eef0f2; padding: 20px; backgroundColor: #e6f0e9; overflow-y: auto; }
        .panel-files { width: 350px; border-right: 1px solid #eee; padding: 20px; backgroundColor: #fcfcfc; overflow-y: auto; }
        .panel-viewer { flex: 1; padding: 10px; display: flex; justifyContent: center; alignItems: center; overflow: hidden; }
        .top-bar { padding: 15px 25px; border-bottom: 1px solid #eef0f2; display: flex; justify-content: space-between; alignItems: center; backgroundColor: #e6f0e9; }

        .dir-item { 
          display: flex; justify-content: space-between; align-items: center; 
          padding: 12px 15px; cursor: pointer; border-radius: 4px; 
          margin-bottom: 5px; position: relative; transition: all 0.2s ease;
        }
        .dir-item:hover { background-color: #d8e6de; }
        .dir-dots { 
          padding: 0 5px; font-size: 1.2em; color: #666; border-radius: 50%; 
          transition: background 0.2s; 
        }
        .dir-dots:hover { background: #ccc; color: #000; }
        
        .dropdown-menu {
          position: absolute; right: 10px; top: 40px; background: white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 6px;
          z-index: 100; min-width: 130px; border: 1px solid #eee;
        }
        .dropdown-item {
          padding: 10px 15px; font-size: 0.9em; color: #333; display: block;
          text-align: left; background: none; border: none; width: 100%; cursor: pointer;
        }
        .dropdown-item:hover { background: #f5f5f5; color: #007A33; }
        .dropdown-item.delete { color: #ff4d4f; }
        .dropdown-item.delete:hover { background: #fff1f0; }

        .modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.6); display: flex; justify-content: center;
          align-items: center; z-index: 1000;
        }
        .modal-content {
          background: white; padding: 30px; border-radius: 12px; width: 400px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
        }
        .modal-footer { display: flex; justify-content: flex-end; gap: 10px; margin-top: 25px; }
        .btn-cancel { background: #f0f0f0; border: none; padding: 10px 18px; border-radius: 6px; cursor: pointer; }
        .btn-confirm-del { background: #ff4d4f; color: white; border: none; padding: 10px 18px; border-radius: 6px; cursor: pointer; }
        .btn-save { background: #007A33; color: white; border: none; padding: 10px 18px; border-radius: 6px; cursor: pointer; }

        .loader-overlay {
          position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
          background: #007A33; color: white; padding: 10px 20px; border-radius: 30px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.2); z-index: 2000; font-weight: bold;
        }

        @media (max-width: 768px) {
          .dashboard-header { flex-direction: column; padding: 10px; min-height: auto; }
          .dashboard-logo { position: static; transform: none; margin-top: 10px; width: 80px; }
          .dashboard-main { flex-direction: column; }
          .panel-dirs, .panel-files { width: 100%; border-right: none; border-bottom: 1px solid #eee; }
          .top-bar { flex-direction: column; gap: 10px; text-align: center; }
          .top-bar div { display: flex; flex-direction: column; gap: 5px; width: 100%; }
          .top-bar button { width: 100%; margin: 0 !important; }
        }
      `}</style>

      {isProcessing && (
        <div className="loader-overlay">Procesando cambios en Azure...</div>
      )}

      {/* Modales */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            {showModal === 'delete' ? (
              <>
                <h3 style={{ color: '#ff4d4f', marginTop: 0 }}>⚠️ Eliminar Directorio</h3>
                <p>¿Estás seguro de que deseas eliminar el directorio <b>"{modalData}"</b>?</p>
                <p style={{ fontSize: '0.9em', color: '#666' }}>Esta acción borrará todas las facturas físicas y registros asociados de forma permanente.</p>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={() => setShowModal(null)}>Cancelar</button>
                  <button className="btn-confirm-del" onClick={onDeleteConfirm} disabled={isProcessing}>Confirmar Eliminación</button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>✏️ Renombrar Directorio</h3>
                <input 
                  type="text" 
                  className="form-control"
                  style={{ width: '100%', padding: '10px', marginTop: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  autoFocus
                />
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={() => setShowModal(null)}>Cancelar</button>
                  <button className="btn-save" onClick={onRenameSubmit} disabled={isProcessing}>Guardar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="dashboard-header">
        <h1 className="andromeda-title" style={{ textAlign: 'center', padding: '20px 10px', margin: 0 }}>Andromeda-Capture</h1>
        <img 
          src="/Logo-Andromeda.png" 
          className="dashboard-logo"
          alt="Andromeda Logo" 
        />
      </div>
      
      <div className="top-bar">
        <h2 style={{ fontSize: '1.5em', margin: 0 }}>Dashboard</h2>
        {username && <p style={{ color: '#007A33', fontSize: '1.1em', fontWeight: 'bold', margin: 0 }}>Welcome, {username}!</p>}
        <div>
          {role === 'superadmin' && (
            <button 
              onClick={() => navigate('/admin-dashboard')} 
              style={{ padding: '10px 20px', fontSize: '1em', marginRight: '10px', backgroundColor: '#007A33', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              Andromeda Adm
            </button>
          )}
          <button 
            onClick={() => {
              const token = sessionStorage.getItem('token');
              window.open(`https://brave-smoke-07922f20f.7.azurestaticapps.net?token=${token}`, '_blank');
            }} 
            style={{ padding: '10px 20px', fontSize: '1em', marginRight: '10px', backgroundColor: '#007A33', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Acceso a "invoices report"
          </button>
          <button onClick={handleLogout} style={{ padding: '10px 20px', fontSize: '1em', border: '2px solid #007A33', color: '#007A33', backgroundColor: 'transparent', borderRadius: '5px', cursor: 'pointer', fontWeight: '500' }}>Logout</button>
        </div>
      </div>

      <div className="dashboard-main">
        {/* Left Panel: Directories */}
        <div className="panel-dirs">
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.2em', marginBottom: '15px' }}>Directories</h3>
            <div style={{ display: 'flex', gap: '5px' }}>
              <input
                type="text"
                value={newDirectory}
                onChange={(e) => setNewDirectory(e.target.value)}
                placeholder="New folder..."
                style={{ padding: '8px', flex: 1, borderRadius: '4px', border: '2px solid #007A33', outline: 'none', minWidth: '0' }}
                disabled={isProcessing}
              />
              <button onClick={handleCreateDirectory} disabled={isProcessing} style={{ backgroundColor: '#007A33', color: 'white', border: 'none', padding: '8px 12px', cursor: 'pointer', borderRadius: '4px' }}>Create</button>
            </div>
          </div>
          <hr style={{ border: '0', borderTop: '1px solid #eee', margin: '20px 0' }} />
          {directories.map((dir) => (
            <div
              key={dir}
              onClick={() => handleDirectoryClick(dir)}
              className="dir-item"
              style={{
                backgroundColor: selectedDirectory === dir ? '#e6f2eb' : 'transparent',
                borderLeft: selectedDirectory === dir ? '4px solid #007A33' : 'none',
                fontWeight: selectedDirectory === dir ? '600' : 'normal',
                color: selectedDirectory === dir ? '#007A33' : '#333'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                📁 {dir}
              </span>
              
              <div 
                className="dir-dots" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setActiveMenu(activeMenu === dir ? null : dir); 
                }}
              >
                ⋮
              </div>

              {activeMenu === dir && (
                <div className="dropdown-menu">
                  <button className="dropdown-item" onClick={(e) => {
                    e.stopPropagation();
                    setModalData(dir);
                    setRenameValue(dir);
                    setShowModal('rename');
                    setActiveMenu(null);
                  }}>✏️ Renombrar</button>
                  <button className="dropdown-item delete" onClick={(e) => {
                    e.stopPropagation();
                    setModalData(dir);
                    setShowModal('delete');
                    setActiveMenu(null);
                  }}>🗑️ Eliminar</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Middle Panel: Files in Selected Directory */}
        <div className="panel-files">
          <h3 style={{ fontSize: '1.2em', marginBottom: '15px' }}>Files {selectedDirectory && `in ${selectedDirectory}`}</h3>
          {selectedDirectory && (
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e6f0e9', border: '2px solid #007A33', borderRadius: '8px' }}>
              <input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} style={{ width: '100%', marginBottom: '10px' }} />
              <button onClick={() => handleFileUpload(selectedDirectory)} style={{ backgroundColor: '#007A33', color: 'white', border: 'none', padding: '10px', cursor: 'pointer', width: '100%', borderRadius: '4px', fontWeight: 'bold' }}>Upload to {selectedDirectory}</button>
            </div>
          )}
          {filesInSelectedDirectory.length > 0 ? (
            filesInSelectedDirectory.map((file) => (
              <div
                key={file.name}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 15px',
                  backgroundColor: selectedFile === file ? '#e6f2eb' : 'transparent',
                  borderLeft: selectedFile === file ? '4px solid #007A33' : 'none',
                  borderRadius: '0 4px 4px 0',
                  marginBottom: '5px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => handleFileClick(file)}
              >
                <span style={{ flexGrow: 1, color: selectedFile === file ? '#007A33' : '#333', fontWeight: selectedFile === file ? '600' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📄 {file.name}
                </span>
                <button
                  onClick={(e) => handleDeleteFile(selectedDirectory, file.name, e)}
                  style={{ marginLeft: '10px', cursor: 'pointer', color: '#ff4d4f', border: 'none', backgroundColor: 'transparent', padding: '5px' }}
                >
                  ✕
                </button>
              </div>
            ))
          ) : (
            <p style={{ color: '#999', textAlign: 'center', marginTop: '40px' }}>{selectedDirectory ? 'No files in this directory.' : 'Select a directory to view files.'}</p>
          )}
        </div>

        {/* Right Panel: Photo Viewer */}
        <div className="panel-viewer">
          {selectedFile ? (
            <img src={selectedFile.url} alt={selectedFile.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <p>Click on a file to view its content.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;